import React, { useState } from 'react';
import Modal from './Modal';
import { ratingsAPI } from '../../services/api';

const RatingModal = ({ isOpen, onClose, dialogId, onSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [leftWithNextStep, setLeftWithNextStep] = useState(null);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (rating === 0) {
      setError('Please provide a rating');
      return;
    }
    if (leftWithNextStep === null) {
      setError('Please answer if you left with a next step');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await ratingsAPI.rateDialog(dialogId, rating, comment, leftWithNextStep);
      if (onSubmitted) onSubmitted();
      onClose();
    } catch (err) {
      console.error('Error submitting rating:', err);
      setError('Failed to submit rating. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Rate your conversation">
      <div className="flex flex-col items-center gap-6">
        <p className="text-[var(--text-secondary)] text-center">
          Your feedback is very important to us. Please rate the quality of the coach's work.
        </p>

        <div className="flex gap-6 justify-center my-2">
          {/* Thumbs Down Button */}
          <button
            type="button"
            className={`flex flex-col items-center justify-center p-4 w-28 h-28 rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 ${
              rating === 1
                ? 'border-red-500 bg-red-50/50 dark:bg-red-950/20 text-red-500 shadow-md'
                : 'border-[var(--border-light)] hover:border-red-400 dark:hover:border-red-600 hover:bg-red-50/10 dark:hover:bg-red-950/10 text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400'
            }`}
            onClick={() => setRating(1)}
          >
            <span
              className="material-symbols-outlined text-4xl leading-none mb-2"
              style={{ fontVariationSettings: rating === 1 ? "'FILL' 1" : "'FILL' 0" }}
            >
              thumb_down
            </span>
            <span className="text-xs font-semibold">Dislike</span>
          </button>

          {/* Thumbs Up Button */}
          <button
            type="button"
            className={`flex flex-col items-center justify-center p-4 w-28 h-28 rounded-2xl border-2 transition-all duration-300 transform hover:scale-105 ${
              rating === 5
                ? 'border-green-500 bg-green-50/50 dark:bg-green-950/20 text-green-500 shadow-md'
                : 'border-[var(--border-light)] hover:border-green-400 dark:hover:border-green-600 hover:bg-green-50/10 dark:hover:bg-green-950/10 text-gray-400 dark:text-gray-500 hover:text-green-500 dark:hover:text-green-400'
            }`}
            onClick={() => setRating(5)}
          >
            <span
              className="material-symbols-outlined text-4xl leading-none mb-2"
              style={{ fontVariationSettings: rating === 5 ? "'FILL' 1" : "'FILL' 0" }}
            >
              thumb_up
            </span>
            <span className="text-xs font-semibold">Like</span>
          </button>
        </div>

        <div className="w-full flex flex-col items-center gap-3">
          <p className="text-[var(--text-secondary)] text-center text-sm font-semibold">
            Did you leave with a next step or action you can take?
          </p>
          <div className="flex gap-4 w-full max-w-xs">
            {/* Yes Button */}
            <button
              type="button"
              className={`flex-1 py-3 px-6 rounded-xl border-2 font-bold transition-all duration-200 ${
                leftWithNextStep === true
                  ? 'border-[var(--tangerine)] bg-orange-50/30 dark:bg-orange-950/10 text-[var(--tangerine)] shadow-sm'
                  : 'border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-gray-200 dark:hover:bg-gray-800'
              }`}
              onClick={() => setLeftWithNextStep(true)}
            >
              Yes
            </button>

            {/* No Button */}
            <button
              type="button"
              className={`flex-1 py-3 px-6 rounded-xl border-2 font-bold transition-all duration-200 ${
                leftWithNextStep === false
                  ? 'border-[var(--tangerine)] bg-orange-50/30 dark:bg-orange-950/10 text-[var(--tangerine)] shadow-sm'
                  : 'border-[var(--border-light)] text-[var(--text-secondary)] hover:bg-gray-200 dark:hover:bg-gray-800'
              }`}
              onClick={() => setLeftWithNextStep(false)}
            >
              No
            </button>
          </div>
        </div>

        <textarea
          className="w-full p-4 rounded-2xl border border-[var(--border-light)] bg-[var(--off-white)] text-[var(--text-primary)] focus:ring-2 focus:ring-[var(--tangerine)] focus:border-transparent outline-none transition-all resize-none h-32"
          placeholder="Leave a comment (optional)..."
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />

        {error && <p className="text-red-500 text-sm font-medium">{error}</p>}

        <div className="flex gap-3 w-full">
            <button
            onClick={onClose}
            className="flex-1 py-4 bg-[var(--off-white)] text-[var(--text-secondary)] rounded-2xl font-bold hover:bg-gray-200 transition-all"
            >
            Later
            </button>
            <button
            onClick={handleSubmit}
            disabled={loading || rating === 0 || leftWithNextStep === null}
            className="flex-[2] py-4 bg-[var(--tangerine)] text-white rounded-2xl font-bold shadow-lg hover:shadow-xl hover:opacity-90 transition-all disabled:opacity-50 disabled:shadow-none"
            >
            {loading ? 'Submitting...' : 'Confirm'}
            </button>
        </div>
      </div>
    </Modal>
  );
};

export default RatingModal;
